/**
 * Conversation state + the ONE send pipeline of the AI Assistant workspace.
 *
 * Everything that happens when the user asks something flows through
 * `dispatch()`:
 *
 *   1. `preflight()` (`preflight.ts`, order of precedence documented there) -
 *      decides whether the question is blocked or diverted: service state,
 *      insufficient tokens, service error, an ambiguous question (E), heavy
 *      analysis confirmation (G). A blocked / diverted send appends that
 *      assistant message after the user's bubble and answers nothing.
 *   2. otherwise the user's bubble + an `analyzing` card (frame H) are appended;
 *   3. after a short fake delay `answerFor()` (demo engine) produces the
 *      scripted answer, the card is replaced by it, and the sample usage is
 *      deducted with `spendTokens()` from `useAiPreview()` - the ONLY place
 *      tokens are spent, and only once there is an answer.
 *
 * Follow-ups of those cards (`chooseOption`, `confirmHeavy`, `cancelHeavy`,
 * `retry`, `stop`) re-enter the same pipeline.
 *
 * Live sends use server quotes and real turns; preview uses only the scripted engine.
 * Browser history is cached per tenant and authenticated principal. An identifier-only
 * journal preserves the original paid-run key for read-only recovery after interruption.
 * Bundled sample conversations remain explicitly labelled preview history.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAiPreview } from '../aiPreview.js';
import { useAiProvider } from '../connect/AiProviderContext.js';
import { AiContractError } from '../connect/errors.ts';
import type { AiTurn } from '../connect/contract.ts';
import { LIVE_DOMAIN_UNAVAILABLE, liveSendRoute } from './liveRoute.ts';
import { liveTurnAnswer } from './liveAnswer.ts';
import {
  CHAT_RECOVERY_ACTION,
  chatUsageUnknown,
  chatStorageKey,
  createChatRecoveryJournal,
  newChatIdentity,
  readChatRunRecovery,
  restoreInterruptedThreads,
  restoreRecoveryConversations,
  uncertainChatAnswer,
  withChatRecoveryAction,
  type ChatRecoveryStorage,
} from './chatSessionRecovery.ts';
import { answerFor, drillPromptText, isConversationAvailable, seedConversation } from './engine.ts';
import { preflight } from './preflight.ts';
import { SAMPLE_CONVERSATIONS } from './sample.ts';
import type {
  AnalyzingMessage,
  AnswerContext,
  AnswerMessage,
  AssistantMessage,
  ChatMessage,
  UserMessage,
  ClarifyMessage,
  ClarifyOption,
  ConfirmHeavyMessage,
  ConversationSummary,
  DrillAction,
  ErrorMessage,
  PathStep,
  PeriodId,
  ReplaySpec,
  ScopeKey,
} from './types.ts';

export { preflight, type PreflightInput, type PreflightResult } from './preflight.ts';

/** Fake analysis time before a scripted answer appears (long enough to read frame H's steps). */
export const ANALYZING_DELAY_MS = 2000;

const NEW_CONVERSATION_TITLE = 'گفتگوی جدید';
const MAX_TITLE_LENGTH = 48;
const DEFAULT_ANALYZING_MODULES: readonly ScopeKey[] = [
  'inventory',
  'sales',
  'procurement',
  'accounting',
  'projects',
];

interface DispatchOptions {
  /** Messages appended before the verdict message (the user's bubble). */
  readonly lead?: readonly ChatMessage[];
  /** Replace this message (retry) instead of appending. */
  readonly replaceId?: string;
  readonly clarified?: boolean;
  readonly confirmedHeavy?: boolean;
  readonly attempt?: number;
}

export interface SendOptions {
  /** Modules ticked in the composer (already limited to the caller's real scope). */
  readonly selected: readonly ScopeKey[];
  readonly period: PeriodId;
  readonly deepSearch?:
    | {
        readonly enabled: boolean;
        readonly scope?: 'web' | 'company' | 'hybrid' | undefined;
      }
    | undefined;
}

export interface ChatSession {
  readonly conversations: readonly ConversationSummary[];
  readonly activeId: string | null;
  readonly activeTitle: string;
  readonly messages: readonly ChatMessage[];
  /** Context of the newest answer of the active conversation (context panel). */
  readonly context: AnswerContext | null;
  /** Id of the newest answer — only its record links are interactive. */
  readonly lastAnswerId: string | null;
  /** An answer is being prepared (frame H) - new sends wait. */
  readonly isThinking: boolean;
  readonly send: (text: string, options: SendOptions) => void;
  /** Frame E: continue a clarification with the picked option. */
  readonly chooseOption: (message: ClarifyMessage, option: ClarifyOption) => void;
  /** Frame G: «اجرا» / «لغو». */
  readonly confirmHeavy: (message: ConfirmHeavyMessage) => void;
  readonly cancelHeavy: (message: ConfirmHeavyMessage) => void;
  /** Frame H: «توقف». */
  readonly stop: (message: AnalyzingMessage) => void;
  /** Frame J: «تلاش دوباره». */
  readonly retry: (message: ErrorMessage) => void;
  readonly drill: (action: DrillAction, from: AnswerMessage) => void;
  readonly select: (id: string) => void;
  readonly newChat: () => void;
  readonly togglePin: (id: string) => void;
  readonly remove: (id: string) => void;
  readonly focusRecord: (label: string) => void;
}

function clipTitle(text: string): string {
  const clean = text.trim().replace(/\s+/g, ' ');
  const chars = Array.from(clean);
  return chars.length > MAX_TITLE_LENGTH ? `${chars.slice(0, MAX_TITLE_LENGTH).join('')}…` : clean;
}

function isAnswer(message: ChatMessage): message is AnswerMessage {
  return message.role === 'assistant' && message.kind === 'answer';
}

function lastAnswerOf(messages: readonly ChatMessage[]): AnswerMessage | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message && isAnswer(message)) return message;
  }
  return null;
}

function usageOf(messages: readonly ChatMessage[]): number {
  return messages.reduce(
    (sum, message) => (isAnswer(message) ? sum + message.footer.tokens : sum),
    0,
  );
}

function liveAnswer(
  id: string,
  text: string,
  toman: number,
  lines: readonly string[],
): AnswerMessage {
  const tokenUsage = toman > 0 ? Math.round(toman * 5) : 0;
  return {
    id,
    role: 'assistant',
    kind: 'answer',
    sampleId: 'server',
    content: {
      text: [text],
      ...(lines.length > 0 ? { source: { lines, postedOnly: false } } : {}),
    },
    footer: {
      tokens: tokenUsage,
      unit: 'توکن',
      sample: false,
      basis: 'وضعیت درخواست',
    },
    context: {
      path: [{ id: 'notice', label: 'گفتگو', kind: 'topic', messageId: id }],
      filters: [],
      sources: [],
    },
    isSample: false,
  };
}

function liveFailureText(error: unknown): string {
  if (error instanceof AiContractError) return error.displayMessage;
  return 'پاسخ دریافت نشد. وضعیت اجرا و مصرف توکن را در گزارش بررسی کنید.';
}

function liveNotice(id: string, text: string): AnswerMessage {
  return {
    id,
    role: 'assistant',
    kind: 'answer',
    sampleId: 'server',
    content: { text: [text] },
    footer: {
      tokens: 0,
      unit: 'توکن',
      sample: false,
      basis: 'بدون اجرای مدل',
    },
    context: {
      path: [{ id: 'notice', label: 'محدوده', kind: 'topic', messageId: id }],
      filters: [],
      sources: [],
    },
    isSample: false,
  };
}

export function useChatSession(): ChatSession {
  const ai = useAiPreview();
  const provider = useAiProvider();
  const granted = useMemo(
    () => ai.scope.filter((item) => item.granted).map((item) => item.key),
    [ai.scope],
  );

  // Always-current values for the delayed answer callback and the card follow-ups.
  const live = useRef({
    granted,
    spendTokens: ai.spendTokens,
    forcedState: ai.forcedState,
    balance: ai.tokens.balance,
    canPurchase: ai.canPurchase,
  });
  live.current = {
    granted,
    spendTokens: ai.spendTokens,
    forcedState: ai.forcedState,
    balance: ai.tokens.balance,
    canPurchase: ai.canPurchase,
  };

  const nextId = useCallback((prefix: string) => newChatIdentity(prefix), []);
  /** Pending answer timers by the id of their `analyzing` message (so «توقف» can cancel one). */
  const timers = useRef(new Map<string, number>());
  /** Live cards the user stopped while quoting; their quote must not run on its own. */
  const stoppedIds = useRef(new Set<string>());
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const isLive = provider.kind === 'live';
  const storageKey = chatStorageKey(ai.base, ai.actorScope);
  const journal = useMemo(() => {
    let storage: ChatRecoveryStorage | null = null;
    try {
      if (typeof window !== 'undefined') storage = window.localStorage;
    } catch {
      /* memory only */
    }
    return createChatRecoveryJournal(storageKey, storage);
  }, [storageKey]);
  const hydratedKey = useRef(storageKey);
  const generation = useRef(0);
  const scope = useRef({ provider, storageKey });
  if (scope.current.provider !== provider || scope.current.storageKey !== storageKey) {
    generation.current += 1;
    scope.current = { provider, storageKey };
  }
  const mounted = useRef(true);
  const requests = useRef(new Map<string, AbortController>());
  const confirmedQuotes = useRef(new Set<string>());
  const submitting = useRef(false);
  const stale = useCallback((mine: number) => !mounted.current || mine !== generation.current, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      requests.current.forEach((request) => request.abort());
      requests.current.clear();
    };
  }, [provider, storageKey]);

  const [activeId, setActiveId] = useState<string | null>(() => {
    if (!storageKey || typeof window === 'undefined') return null;
    try {
      const saved = localStorage.getItem(`${storageKey}:active`);
      const parsed: unknown = saved ? JSON.parse(saved) : null;
      return typeof parsed === 'string' ? parsed : null;
    } catch {
      return null;
    }
  });
  const [created, setCreated] = useState<readonly ConversationSummary[]>(() => {
    if (!storageKey || typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(`${storageKey}:conversations`);
      const existing = saved ? (JSON.parse(saved) as readonly ConversationSummary[]) : [];
      return restoreRecoveryConversations(existing, journal.list());
    } catch {
      return [];
    }
  });
  const [pinOverride, setPinOverride] = useState<Readonly<Record<string, boolean>>>(() => {
    if (!storageKey || typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem(`${storageKey}:pin`);
      const parsed: unknown = saved ? JSON.parse(saved) : {};
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.fromEntries(
            Object.entries(parsed).filter(([, value]) => typeof value === 'boolean'),
          )
        : {};
    } catch {
      return {};
    }
  });
  const [removed, setRemoved] = useState<readonly string[]>(() => {
    if (!storageKey || typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(`${storageKey}:removed`);
      const parsed: unknown = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === 'string')
        : [];
    } catch {
      return [];
    }
  });
  const [threads, setThreads] = useState<Readonly<Record<string, readonly ChatMessage[]>>>(() => {
    if (!storageKey || typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem(`${storageKey}:threads`);
      const existing = saved
        ? (JSON.parse(saved) as Readonly<Record<string, readonly ChatMessage[]>>)
        : {};
      return restoreInterruptedThreads(existing, journal.list(), journal.dispatched());
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!storageKey || hydratedKey.current !== storageKey || typeof window === 'undefined') return;
    try {
      localStorage.setItem(`${storageKey}:conversations`, JSON.stringify(created));
      localStorage.setItem(`${storageKey}:threads`, JSON.stringify(threads));
      localStorage.setItem(`${storageKey}:pin`, JSON.stringify(pinOverride));
      localStorage.setItem(`${storageKey}:removed`, JSON.stringify(removed));
      if (activeId !== null) {
        localStorage.setItem(`${storageKey}:active`, JSON.stringify(activeId));
      } else {
        localStorage.removeItem(`${storageKey}:active`);
      }
    } catch {
      // LocalStorage access errors are non-fatal
    }
  }, [storageKey, created, threads, pinOverride, removed, activeId]);

  /** Bundled sample history, limited to what the caller's real scope allows. */
  const seeded = useMemo(() => {
    const map: Record<string, readonly ChatMessage[]> = {};
    for (const conversation of SAMPLE_CONVERSATIONS) {
      if (isConversationAvailable(conversation, granted)) {
        map[conversation.id] = seedConversation(conversation, granted);
      }
    }
    return map;
  }, [granted]);
  const seededRef = useRef(seeded);
  seededRef.current = seeded;

  const threadOf = useCallback(
    (id: string): readonly ChatMessage[] => threads[id] ?? seeded[id] ?? [],
    [threads, seeded],
  );

  const conversations = useMemo<readonly ConversationSummary[]>(() => {
    const samples = isLive
      ? []
      : SAMPLE_CONVERSATIONS.filter(
          (conversation) => seeded[conversation.id] && !removed.includes(conversation.id),
        ).map(
          (conversation): ConversationSummary => ({
            id: conversation.id,
            title: conversation.title,
            pinned: pinOverride[conversation.id] ?? conversation.pinned,
            bucket: conversation.bucket,
            tokens: usageOf(threads[conversation.id] ?? seeded[conversation.id] ?? []),
            isSample: true,
          }),
        );
    const mine = created
      .filter((conversation) => !removed.includes(conversation.id))
      .map(
        (conversation): ConversationSummary => ({
          ...conversation,
          pinned: pinOverride[conversation.id] ?? conversation.pinned,
          tokens: usageOf(threads[conversation.id] ?? []),
          usageUnknown: chatUsageUnknown(threads[conversation.id] ?? []),
        }),
      );
    return [...mine, ...samples];
  }, [isLive, created, seeded, removed, pinOverride, threads]);

  const activeConversation = conversations.find((conversation) => conversation.id === activeId);
  const messages = useMemo(
    () => (activeId && activeConversation ? threadOf(activeId) : []),
    [activeId, activeConversation, threadOf],
  );
  const lastAnswer = useMemo(() => lastAnswerOf(messages), [messages]);
  const isThinking = messages.some((m) => m.role === 'assistant' && m.kind === 'analyzing');

  const appendTo = useCallback((conversationId: string, added: readonly ChatMessage[]) => {
    setThreads((current) => ({
      ...current,
      [conversationId]: [
        ...(current[conversationId] ?? seededRef.current[conversationId] ?? []),
        ...added,
      ],
    }));
  }, []);

  const replaceIn = useCallback((conversationId: string, messageId: string, next: ChatMessage) => {
    setThreads((current) => ({
      ...current,
      [conversationId]: (current[conversationId] ?? seededRef.current[conversationId] ?? []).map(
        (message) => (message.id === messageId ? next : message),
      ),
    }));
  }, []);

  /** Prepares the scripted answer after the fake delay and replaces the `analyzing` card with it. */
  const schedule = useCallback(
    (
      conversationId: string,
      analyzingId: string,
      request: ReplaySpec['request'],
      prev: AnswerContext | undefined,
    ) => {
      const mine = generation.current;
      const timer = window.setTimeout(() => {
        timers.current.delete(analyzingId);
        if (stale(mine)) return;
        const answer = answerFor(request, nextId('a'), {
          granted: live.current.granted,
          prev,
        });
        replaceIn(conversationId, analyzingId, answer);
        // The one and only spend: after an answer exists (notices cost nothing).
        if (answer.footer.tokens > 0) live.current.spendTokens(answer.footer.tokens);
      }, ANALYZING_DELAY_MS);
      timers.current.set(analyzingId, timer);
    },
    [nextId, replaceIn, stale],
  );

  /** Runs the preflight and either appends the diverting message or starts the analysis. */
  const dispatch = useCallback(
    (
      conversationId: string,
      replay: ReplaySpec,
      prev: AnswerContext | undefined,
      options: DispatchOptions = {},
    ) => {
      const messageId = options.replaceId ?? nextId('m');
      const verdict = preflight({
        messageId,
        replay,
        granted: live.current.granted,
        forcedState: live.current.forcedState,
        tokenBalance: live.current.balance,
        canPurchase: live.current.canPurchase,
        ...(options.clarified ? { clarified: true } : {}),
        ...(options.confirmedHeavy ? { confirmedHeavy: true } : {}),
        ...(options.attempt !== undefined ? { attempt: options.attempt } : {}),
      });
      let next: AssistantMessage;
      if (verdict.allow) {
        next = { id: messageId, role: 'assistant', kind: 'analyzing', modules: verdict.modules };
        schedule(conversationId, messageId, replay.request, prev);
      } else {
        next = verdict.message;
      }
      if (options.replaceId) replaceIn(conversationId, options.replaceId, next);
      else appendTo(conversationId, [...(options.lead ?? []), next]);
    },
    [nextId, schedule, appendTo, replaceIn],
  );

  /** Runs a server quote into the waiting card `cardId` and replaces it with the answer. */
  const runQuote = useCallback(
    async (conversationId: string, cardId: string, quoteId: string) => {
      if (provider.kind !== 'live') return;
      const http = provider;
      const mine = generation.current;
      const controller = new AbortController();
      requests.current.set(cardId, controller);
      const record = { conversationId, cardId, quoteId, idempotencyKey: crypto.randomUUID() };
      // Save only the original identifiers before dispatch, independently of React's delayed cache effect.
      journal.put(record);
      try {
        const turn: AiTurn = await http.createRun(
          {
            quoteId,
            idempotencyKey: record.idempotencyKey,
          },
          { signal: controller.signal },
        );
        if (stale(mine)) return;
        if (turn.state !== 'pending') journal.remove(cardId);
        const answer = liveTurnAnswer(cardId, turn);
        replaceIn(
          conversationId,
          cardId,
          turn.state === 'pending' ? withChatRecoveryAction(answer) : answer,
        );
      } catch {
        if (stale(mine)) return;
        // A transport/API error alone cannot prove that the paid execution never started.
        replaceIn(conversationId, cardId, uncertainChatAnswer(cardId, true));
      } finally {
        if (requests.current.get(cardId) === controller) requests.current.delete(cardId);
      }
    },
    [provider, replaceIn, journal, stale],
  );

  const recoverRun = useCallback(
    async (cardId: string) => {
      if (provider.kind !== 'live' || requests.current.has(cardId)) return;
      const record = journal.find(cardId);
      if (!record) return;
      const mine = generation.current;
      const controller = new AbortController();
      requests.current.set(cardId, controller);
      replaceIn(record.conversationId, cardId, uncertainChatAnswer(cardId, true, true));
      try {
        const turn = await readChatRunRecovery(provider, record, controller.signal);
        if (stale(mine)) return;
        if (!turn) replaceIn(record.conversationId, cardId, uncertainChatAnswer(cardId, true));
        else {
          if (turn.state !== 'pending') journal.remove(cardId);
          const answer = liveTurnAnswer(cardId, turn);
          replaceIn(
            record.conversationId,
            cardId,
            turn.state === 'pending' ? withChatRecoveryAction(answer) : answer,
          );
        }
      } catch {
        if (!stale(mine))
          replaceIn(record.conversationId, cardId, uncertainChatAnswer(cardId, true));
      } finally {
        if (requests.current.get(cardId) === controller) requests.current.delete(cardId);
      }
    },
    [provider, journal, replaceIn, stale],
  );

  const startLive = useCallback(
    (conversationId: string, userMessage: UserMessage, question: string) => {
      if (provider.kind !== 'live') return;
      const http = provider;
      const cardId = nextId('m');
      const mine = generation.current;
      const controller = new AbortController();
      requests.current.set(cardId, controller);
      appendTo(conversationId, [
        userMessage,
        {
          id: cardId,
          role: 'assistant',
          kind: 'analyzing',
          live: true,
          modules: userMessage.scope.length > 0 ? userMessage.scope : DEFAULT_ANALYZING_MODULES,
        },
      ]);
      void (async () => {
        try {
          // The central assistant retrieves the relevant permitted company facts itself.
          // A caller with only one module grant still has a general conversation here.
          const quote = await http.createQuote(
            {
              question,
              domain: 'sakhtar',
              period: userMessage.period,
              ...(userMessage.deepSearch !== undefined
                ? { deepSearch: userMessage.deepSearch }
                : {}),
            },
            { signal: controller.signal },
          );
          if (stale(mine) || stoppedIds.current.has(cardId)) return;
          if (!quote.dataSufficient) {
            replaceIn(
              conversationId,
              cardId,
              liveAnswer(cardId, quote.message ?? 'داده کافی موجود نیست', 0, []),
            );
            return;
          }
          // A card the user stopped while quoting stays stopped; its quote never runs.
          if (stoppedIds.current.has(cardId)) return;
          // Standard workflow answers run straight away; workflow > 20,000 tokens requires confirmation
          if (!quote.confirmationRequired) {
            replaceIn(conversationId, cardId, {
              id: cardId,
              role: 'assistant',
              kind: 'analyzing',
              live: true,
              modules: userMessage.scope.length > 0 ? userMessage.scope : DEFAULT_ANALYZING_MODULES,
              stoppable: false,
            });
            await runQuote(conversationId, cardId, quote.quoteId);
            return;
          }
          const balance = await http.getBalance({ signal: controller.signal });
          if (stale(mine) || stoppedIds.current.has(cardId)) return;
          const availableTokens = balance.rawTokens?.available
            ? Number(balance.rawTokens.available)
            : balance.available.toman === null
              ? null
              : Number(balance.available.toman) * 5;
          const estimatedTokens =
            quote.estimatedTotalTokens ??
            (quote.reserveInputTokens ?? 0) + (quote.reserveOutputTokens ?? 0);
          const estimate =
            estimatedTokens > 0
              ? estimatedTokens
              : quote.amountToman === null
                ? 0
                : Number(quote.amountToman) * 5;
          const unit = 'توکن';
          const replay: ReplaySpec = {
            request: { type: 'prompt', text: question, selected: userMessage.scope },
            userText: question,
            selected: userMessage.scope,
            period: userMessage.period,
            ...(userMessage.deepSearch !== undefined ? { deepSearch: userMessage.deepSearch } : {}),
          };
          const confirm: ConfirmHeavyMessage = {
            id: cardId,
            role: 'assistant',
            kind: 'confirm-heavy',
            estimate,
            balance: availableTokens,
            unit,
            quoteId: quote.quoteId,
            reason: 'costing',
            status: 'pending',
            note: 'برآورد مجموع پردازش این پرسش بیش از ۲۰٬۰۰۰ توکن است. تا تأیید «اجرا» اعتباری رزرو نمی‌شود و پس از پایان، فقط مصرف قطعی کسر می‌گردد.',
            replay,
          };
          replaceIn(conversationId, cardId, confirm);
        } catch (error) {
          if (stale(mine) || stoppedIds.current.has(cardId)) return;
          replaceIn(conversationId, cardId, liveAnswer(cardId, liveFailureText(error), 0, []));
        } finally {
          if (requests.current.get(cardId) === controller) requests.current.delete(cardId);
        }
      })();
    },
    [provider, nextId, appendTo, replaceIn, runQuote, stale],
  );

  const finishLive = useCallback(
    (conversationId: string, message: ConfirmHeavyMessage) => {
      if (provider.kind !== 'live' || message.quoteId === undefined) return;
      const cardId = nextId('m');
      appendTo(conversationId, [
        {
          id: cardId,
          role: 'assistant',
          kind: 'analyzing',
          live: true,
          modules:
            message.replay.selected.length > 0
              ? message.replay.selected
              : DEFAULT_ANALYZING_MODULES,
          stoppable: false,
        },
      ]);
      void runQuote(conversationId, cardId, message.quoteId);
    },
    [provider, nextId, appendTo, runQuote],
  );

  /** THE send pipeline. See the file header. */
  const submit = useCallback(
    (replay: ReplaySpec, prev: AnswerContext | undefined, clarified = false) => {
      if (isThinking || submitting.current) return;
      submitting.current = true;
      queueMicrotask(() => {
        submitting.current = false;
      });

      let conversationId = activeId;
      if (!conversationId || !conversations.some((c) => c.id === conversationId)) {
        conversationId = nextId('n');
        const fresh: ConversationSummary = {
          id: conversationId,
          title: clipTitle(replay.userText) || NEW_CONVERSATION_TITLE,
          pinned: false,
          bucket: 'today',
          tokens: 0,
          isSample: false,
        };
        setCreated((current) => [fresh, ...current]);
        setActiveId(conversationId);
      }

      const userMessage: UserMessage = {
        id: nextId('u'),
        role: 'user',
        kind: 'prompt',
        text: replay.userText,
        scope: replay.selected,
        period: replay.period,
        ...(replay.deepSearch !== undefined ? { deepSearch: replay.deepSearch } : {}),
      };
      const route = liveSendRoute({
        live: provider.kind === 'live',
        requestType: replay.request.type,
        selected: replay.selected,
      });
      if (route === 'inventory' || route === 'live') {
        startLive(conversationId, userMessage, replay.userText);
        return;
      }
      if (route === 'unavailable') {
        appendTo(conversationId, [userMessage, liveNotice(nextId('m'), LIVE_DOMAIN_UNAVAILABLE)]);
        return;
      }
      dispatch(conversationId, replay, prev, { lead: [userMessage], clarified });
    },
    [isThinking, activeId, conversations, nextId, dispatch, provider, startLive, appendTo],
  );

  const send = useCallback(
    (text: string, options: SendOptions) => {
      const trimmed = text.trim();
      if (trimmed === '') return;
      submit(
        {
          request: { type: 'prompt', text: trimmed, selected: options.selected },
          userText: trimmed,
          selected: options.selected,
          period: options.period,
          ...(options.deepSearch !== undefined ? { deepSearch: options.deepSearch } : {}),
        },
        lastAnswer?.context,
      );
    },
    [submit, lastAnswer],
  );

  const drill = useCallback(
    (action: DrillAction, from: AnswerMessage) => {
      if (action.kind === 'chip' && action.chipId === CHAT_RECOVERY_ACTION) {
        void recoverRun(from.id);
        return;
      }
      const isLive = provider.kind === 'live';
      submit(
        {
          request: isLive
            ? { type: 'prompt', text: drillPromptText(action), selected: live.current.granted }
            : { type: 'drill', action },
          userText: drillPromptText(action),
          selected: live.current.granted,
          period: 'this-month',
        },
        from.context,
      );
    },
    [submit, provider, recoverRun],
  );

  /** Frame E: the picked option becomes the user's answer and the question continues with it. */
  const chooseOption = useCallback(
    (message: ClarifyMessage, option: ClarifyOption) => {
      if (isThinking || !activeId || message.chosenId !== undefined) return;
      replaceIn(activeId, message.id, { ...message, chosenId: option.id });
      if (provider.kind === 'live') {
        appendTo(activeId, [
          {
            id: nextId('u'),
            role: 'user',
            kind: 'prompt',
            text: option.label,
            scope: message.selected,
            period: message.period,
          },
          liveNotice(nextId('m'), LIVE_DOMAIN_UNAVAILABLE),
        ]);
        return;
      }
      const userMessage: ChatMessage = {
        id: nextId('u'),
        role: 'user',
        kind: 'prompt',
        text: option.label,
        scope: message.selected,
        period: message.period,
      };
      dispatch(
        activeId,
        {
          request: { type: 'prompt', text: option.prompt, selected: message.selected },
          userText: option.label,
          selected: message.selected,
          period: message.period,
        },
        lastAnswer?.context,
        { lead: [userMessage], clarified: true },
      );
    },
    [isThinking, activeId, lastAnswer, nextId, replaceIn, dispatch, provider, appendTo],
  );

  /** Frame G «اجرا»: re-runs the preflight (the balance or state may have changed) without asking again. */
  const confirmHeavy = useCallback(
    (message: ConfirmHeavyMessage) => {
      if (isThinking || !activeId || message.status !== 'pending') return;
      if (message.quoteId !== undefined) {
        if (journal.wasDispatched(message.quoteId)) {
          replaceIn(activeId, message.id, { ...message, status: 'confirmed' });
          return;
        }
        if (confirmedQuotes.current.has(message.quoteId)) return;
        confirmedQuotes.current.add(message.quoteId);
      }
      replaceIn(activeId, message.id, { ...message, status: 'confirmed' });
      if (message.quoteId !== undefined) {
        finishLive(activeId, message);
        return;
      }
      if (provider.kind === 'live') {
        appendTo(activeId, [liveNotice(nextId('m'), LIVE_DOMAIN_UNAVAILABLE)]);
        return;
      }
      dispatch(activeId, message.replay, lastAnswer?.context, { confirmedHeavy: true });
    },
    [
      isThinking,
      activeId,
      lastAnswer,
      replaceIn,
      dispatch,
      finishLive,
      provider,
      appendTo,
      nextId,
      journal,
    ],
  );

  /** Frame G «لغو»: nothing is sent and nothing is charged. */
  const cancelHeavy = useCallback(
    (message: ConfirmHeavyMessage) => {
      if (!activeId || message.status !== 'pending') return;
      replaceIn(activeId, message.id, { ...message, status: 'cancelled' });
    },
    [activeId, replaceIn],
  );

  /** Frame H «توقف»: drops the pending answer - no answer, no charge. */
  const stop = useCallback(
    (message: AnalyzingMessage) => {
      if (!activeId) return;
      if (message.live && message.stoppable === false) return;
      stoppedIds.current.add(message.id);
      requests.current.get(message.id)?.abort();
      const timer = timers.current.get(message.id);
      if (timer !== undefined) window.clearTimeout(timer);
      timers.current.delete(message.id);
      replaceIn(activeId, message.id, { id: message.id, role: 'assistant', kind: 'stopped' });
    },
    [activeId, replaceIn],
  );

  /** Frame J «تلاش دوباره»: the same question goes through the preflight again, in place. */
  const retry = useCallback(
    (message: ErrorMessage) => {
      if (isThinking || !activeId) return;
      if (provider.kind === 'live') {
        replaceIn(activeId, message.id, liveNotice(message.id, LIVE_DOMAIN_UNAVAILABLE));
        return;
      }
      dispatch(activeId, message.replay, lastAnswer?.context, {
        replaceId: message.id,
        attempt: message.attempt + 1,
      });
    },
    [isThinking, activeId, lastAnswer, dispatch, provider, replaceIn],
  );

  const focusRecord = useCallback(
    (label: string) => {
      if (!activeId || !lastAnswer) return;
      const messageId = lastAnswer.id;
      setThreads((current) => ({
        ...current,
        [activeId]: (current[activeId] ?? seededRef.current[activeId] ?? []).map((message) => {
          if (message.id !== messageId || !isAnswer(message)) return message;
          const base = message.context.path.filter((step) => step.kind !== 'record');
          const record: PathStep = {
            id: `${messageId}:record`,
            label,
            kind: 'record',
            messageId,
          };
          return { ...message, context: { ...message.context, path: [...base, record] } };
        }),
      }));
    },
    [activeId, lastAnswer],
  );

  const select = useCallback((id: string) => setActiveId(id), []);
  const newChat = useCallback(() => setActiveId(null), []);

  const togglePin = useCallback(
    (id: string) => {
      const current = conversations.find((conversation) => conversation.id === id);
      if (!current) return;
      setPinOverride((overrides) => ({ ...overrides, [id]: !current.pinned }));
    },
    [conversations],
  );

  const remove = useCallback((id: string) => {
    setRemoved((current) => [...current, id]);
    setActiveId((current) => (current === id ? null : current));
  }, []);

  return {
    conversations,
    activeId: activeConversation ? activeId : null,
    activeTitle: activeConversation?.title ?? NEW_CONVERSATION_TITLE,
    messages,
    context: lastAnswer?.context ?? null,
    lastAnswerId: lastAnswer?.id ?? null,
    isThinking,
    send,
    chooseOption,
    confirmHeavy,
    cancelHeavy,
    stop,
    retry,
    drill,
    select,
    newChat,
    togglePin,
    remove,
    focusRecord,
  };
}
